/**
 * Rewarded Ad Manager
 * Handles integration with Google Ad Manager for rewarded video ads
 * Falls back to test mode if ads are not available
 */
export default class RewardedAdManager {
    private isAdManagerLoaded: boolean = false;
    private isTestMode: boolean = false;
    private adSlot: any = null;
    private onRewardCallback: (() => void) | null = null;

    constructor() {
        this.loadAdManager();
    }

    /**
     * Load Google Ad Manager script
     */
    private loadAdManager(): void {
        // Check if we're in a test environment or if ads should be disabled
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        if (isLocalhost) {
            console.log('RewardedAdManager: Running in test mode (localhost)');
            this.isTestMode = true;
            return;
        }

        // Load Google Ad Manager (GPT) script
        if (window.googletag && (window.googletag as any).apiReady) {
            this.initializeAdManager();
        } else {
            const script = document.createElement('script');
            script.src = 'https://www.googletagservices.com/tag/js/gpt.js';
            script.async = true;
            script.onload = () => {
                this.initializeAdManager();
            };
            script.onerror = () => {
                console.warn('RewardedAdManager: Failed to load Google Ad Manager, using test mode');
                this.isTestMode = true;
            };
            document.head.appendChild(script);
        }
    }

    /**
     * Initialize Google Ad Manager
     */
    private initializeAdManager(): void {
        try {
            window.googletag = window.googletag || { cmd: [] };
            window.googletag.cmd.push(() => {
                // Enable rewarded ads
                window.googletag.pubads().enableSingleRequest();
                window.googletag.pubads().addEventListener('rewardedSlotReady', (event: any) => {
                    console.log('RewardedAdManager: Rewarded ad slot ready');
                });
                window.googletag.pubads().addEventListener('rewardedSlotGranted', (event: any) => {
                    console.log('RewardedAdManager: Reward granted');
                    if (this.onRewardCallback) {
                        this.onRewardCallback();
                        this.onRewardCallback = null;
                    }
                });
                window.googletag.pubads().addEventListener('rewardedSlotClosed', (event: any) => {
                    console.log('RewardedAdManager: Rewarded ad closed');
                    this.cleanup();
                });
                this.isAdManagerLoaded = true;
            });
        } catch (error) {
            console.warn('RewardedAdManager: Error initializing Ad Manager, using test mode', error);
            this.isTestMode = true;
        }
    }

    /**
     * Check if ads are available
     */
    public isAvailable(): boolean {
        return this.isAdManagerLoaded || this.isTestMode;
    }

    /**
     * Show a rewarded ad
     * @param adUnitPath - The ad unit path from Google Ad Manager (e.g., '/12345678/rewarded_video')
     * @param onReward - Callback when reward is granted
     * @param onError - Callback when ad fails to load/show
     */
    public showRewardedAd(
        adUnitPath: string,
        onReward: () => void,
        onError?: (error: string) => void
    ): void {
        this.onRewardCallback = onReward;

        if (this.isTestMode) {
            // Simulate ad viewing in test mode
            console.log('RewardedAdManager: Test mode - simulating ad view');
            setTimeout(() => {
                // Simulate 3 second "ad" viewing
                const modal = this.createTestAdModal();
                document.body.appendChild(modal);
                
                setTimeout(() => {
                    document.body.removeChild(modal);
                    if (this.onRewardCallback) {
                        this.onRewardCallback();
                        this.onRewardCallback = null;
                    }
                }, 3000);
            }, 100);
            return;
        }

        if (!this.isAdManagerLoaded) {
            const errorMsg = 'Ad Manager not loaded yet';
            console.error('RewardedAdManager:', errorMsg);
            if (onError) onError(errorMsg);
            return;
        }

        try {
            window.googletag.cmd.push(() => {
                // Define the rewarded ad slot
                this.adSlot = window.googletag.defineSlot(adUnitPath, [1, 1], 'rewarded-ad-container')
                    .addService(window.googletag.pubads());

                // Request the ad
                window.googletag.enableServices();
                window.googletag.display('rewarded-ad-container');
            });
        } catch (error) {
            const errorMsg = `Failed to show ad: ${error}`;
            console.error('RewardedAdManager:', errorMsg);
            if (onError) onError(errorMsg);
        }
    }

    /**
     * Create a test ad modal for development
     */
    private createTestAdModal(): HTMLDivElement {
        const modal = document.createElement('div');
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.9);
            z-index: 10000;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-family: 'Fredoka', sans-serif;
        `;

        const content = document.createElement('div');
        content.style.cssText = `
            background: #FFD93D;
            border: 4px solid #000;
            padding: 2rem;
            border-radius: 1rem;
            text-align: center;
            box-shadow: 8px 8px 0px rgba(0, 0, 0, 0.2);
        `;

        const title = document.createElement('h2');
        title.textContent = '🎬 TEST AD';
        title.style.cssText = `
            font-size: 2rem;
            font-weight: 900;
            color: #000;
            margin: 0 0 1rem 0;
        `;

        const message = document.createElement('p');
        message.textContent = 'In production, a video ad would play here.';
        message.style.cssText = `
            font-size: 1rem;
            color: #333;
            margin: 0;
        `;

        content.appendChild(title);
        content.appendChild(message);
        modal.appendChild(content);

        return modal;
    }

    /**
     * Cleanup ad slot
     */
    private cleanup(): void {
        if (this.adSlot && window.googletag) {
            try {
                window.googletag.cmd.push(() => {
                    window.googletag.destroySlots([this.adSlot]);
                });
            } catch (error) {
                console.warn('RewardedAdManager: Error cleaning up ad slot', error);
            }
        }
        this.adSlot = null;
    }

    /**
     * Destroy the ad manager
     */
    public destroy(): void {
        this.cleanup();
        this.onRewardCallback = null;
    }
}

// Extend Window interface for googletag
declare global {
    interface Window {
        googletag: any;
    }
}

